import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  AddQuadrantMappingRequest,
  MeterDto,
  Quadrant,
  QuadrantMappingsService,
} from '@app/api/dmc';
import { FormApiService } from '@app/core/services/form-api.service';
import { TranslatePipe } from '@ngx-translate/core';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { SelectDirective } from '@shared/components/input/select/select.directive';
import { InputDateDirective } from '@shared/components/input/date/input-date.directive';
import { ToggleComponent } from '@shared/components/input/toggle/toggle.component';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { enumToKeyValue } from '@app/core/utils/enum-to-key-value';
import { AlertService } from '@app/core/services/alert.service';

@Component({
  selector: 'app-meter-add-quadrant-mapping',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    InputGroupComponent,
    LabelDirective,
    SelectDirective,
    InputDateDirective,
    ToggleComponent,
  ],
  templateUrl: './meter-add-quadrant-mapping.component.html',
})
export class MeterAddQuadrantMappingComponent implements OnInit {
  public meter?: MeterDto;
  private initialized = false;

  public activeQuadrantList: KeyValue[] = enumToKeyValue(Quadrant, {
    includeKeys: ['Consumption', 'Production'],
    prefixValue: 'enum.quadrant.',
  });

  public reactiveQuadrantList: KeyValue[] = enumToKeyValue(Quadrant, {
    includeKeys: ['ReactivePositive', 'ReactiveNegative'],
    prefixValue: 'enum.quadrant.',
  });

  public form = new FormGroup({
    validFrom:    new FormControl<string | null>(null, Validators.required),
    mappingA14:   new FormControl<string | null>(null, Validators.required),
    mappingA23:   new FormControl<string | null>(null, Validators.required),
    mappingR12:   new FormControl<string | null>(null, Validators.required),
    mappingR34:   new FormControl<string | null>(null, Validators.required),
    resendTimeSeries: new FormControl<boolean>(false),
    resendValidTo: new FormControl<string | null>(null),
  });

  constructor(
    private readonly quadrantMappingsService: QuadrantMappingsService,
    private readonly formApiService: FormApiService,
    private readonly alertService: AlertService,
  ) {}

  public ngOnInit(): void {
    this.alertService.info('modal', 'quadrantMappingModal.syncWarning');
    this.initFormFromMeter();
  }

  private initFormFromMeter(): void {
    if (this.initialized || !this.meter) {
      if (!this.initialized) {
        setTimeout(() => this.initFormFromMeter());
      }
      return;
    }
    this.initialized = true;
    this.form.patchValue({
      mappingA14: this.meter.mappingA14 ?? Quadrant.Consumption,
      mappingA23: this.meter.mappingA23 ?? Quadrant.Production,
      mappingR12: this.meter.mappingR12 ?? Quadrant.ReactivePositive,
      mappingR34: this.meter.mappingR34 ?? Quadrant.ReactiveNegative,
    });
  }

  public submit(): void {
    if (!this.meter?.id) {
      return;
    }

    const meterId = this.meter.id;
    const v = this.form.value;

    this.formApiService.submit(
      this.form,
      () => this.quadrantMappingsService.metersIdQuadrantMappingsPost(meterId, this.buildRequest(v)),
      { successMessage: 'quadrantMappingModal.success', closeModal: true },
    );
  }

  private buildRequest(v: typeof this.form.value): AddQuadrantMappingRequest {
    return {
      quadrantMapping: {
        mappingA14: v.mappingA14 as Quadrant,
        mappingA23: v.mappingA23 as Quadrant,
        mappingR12: v.mappingR12 as Quadrant,
        mappingR34: v.mappingR34 as Quadrant,
        validFrom: v.validFrom!,
        validTo: null,
      },
      resendTimeSeries: v.resendTimeSeries
        ? {
          validTo: v.resendValidTo ?? null,
        }
        : null,
    };
  }
}
